import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import WidgetFrame from 'src/components/widgets/WidgetFrame/WidgetFrame.vue';
import icon from 'assets/img/dashboards/dash-sentiment.svg';
import errorIcon from 'assets/icons/alert-bubble.svg';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import Timeline from 'src/components/project/analysis/results/widgets/Timeline.vue';
import { getCsvData, regroupData as regroup } from './SentimentTimeline.utils';
import { adjustColor, getAggregationOffset, getDataAbsMax, makeTimelineSlide } from '../DataWidgetUtils';
import { computed, defineComponent, onMounted, ref, watch } from '@vue/composition-api';
import StatsRow from './StatsRow.vue';
import WidgetMessagePanel from 'components/widgets/WidgetMessagePanel/WidgetMessagePanel.vue';
export default defineComponent({
    components: {
        WidgetFrame,
        DownloadExportButton,
        Timeline,
        WidgetMenu,
        StatsRow,
        WidgetMessagePanel,
    },
    props: {
        isZoomed: { type: Boolean, required: false, default: false },
        zoomToRoute: { type: Object, required: false, default: null },
        sliceOneName: { type: String, required: true },
        sliceTwoName: { type: String, required: true },
        sliceOneDataTotal: { type: Object, required: false, default: null },
        sliceTwoDataTotal: { type: Object, required: false, default: null },
        sliceOneData: { type: Object, required: false, default: null },
        sliceTwoData: { type: Object, required: false, default: null },
        sliceOneFilters: { type: Array, required: false, default: () => [] },
        sliceTwoFilters: { type: Array, required: false, default: () => [] },
        group: { type: String, required: false, default: 'overall__' },
        exportName: { type: String, required: false, default: '' },
        devMode: { type: Boolean, required: false, default: false },
        banner: { type: Object, default: () => null, required: false },
        dateFields: { type: Array, required: true },
        defaultDateField: { type: String, required: false, default: null },
        weekStart: { type: String, required: false, default: null },
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
        dayFirstDates: { type: Boolean, required: false, default: false },
    },
    setup(props, { emit }) {
        const root = ref(null);
        const menuSelections = ref({
            'Date Field': '',
            'Display': 'Values',
            'Resolution': 'Monthly',
        });
        const timelineSeries = ref([]);
        const yRange = ref([0, 1]);
        const records = ref({});
        const querySentiment = computed(() => {
            const sliceOne = props.sliceOneDataTotal?.data?.payload?.find((p) => p.group__ == props.group);
            const sliceTwo = props.sliceTwoDataTotal?.data?.payload?.find((p) => p.group__ == props.group);
            const format = (value) => {
                return (value ?? 0).toFixed(0);
            };
            return [{
                    positive: format(sliceOne?.['sentiment__|positive%__']),
                    negative: format(sliceOne?.['sentiment__|negative%__']),
                    mixed: format(sliceOne?.['sentiment__|mixed%__']),
                    neutral: format(sliceOne?.['sentiment__|neutral%__']),
                }, {
                    positive: format(sliceTwo?.['sentiment__|positive%__']),
                    negative: format(sliceTwo?.['sentiment__|negative%__']),
                    mixed: format(sliceTwo?.['sentiment__|mixed%__']),
                    neutral: format(sliceTwo?.['sentiment__|neutral%__']),
                }];
        });
        const hasDate = computed(() => {
            return props.dateFields.length > 0;
        });
        const isLoading = computed(() => {
            return props.sliceOneDataTotal?.status === 'fetching' ||
                props.sliceTwoDataTotal?.status === 'fetching' ||
                props.sliceOneData?.status === 'fetching' ||
                props.sliceTwoData?.status === 'fetching';
        });
        const error = computed(() => {
            return props.sliceOneDataTotal?.error ?? props.sliceTwoDataTotal?.error;
        });
        const refresh = () => {
            window.location.reload();
        };
        const contact = () => {
            try {
                window.Intercom('show');
            }
            catch {
                console.warn('intercom show failed');
            }
        };
        const reload = () => {
            fetchData(true);
        };
        const displayOptions = computed(() => {
            return {
                label: 'Frequency (%)',
                numberType: 'percentage',
                yAxisLabel: 'Values',
                yCapValue: 2,
                yMultipleOf: 0.1,
            };
        });
        const menus = computed(() => {
            const options = [{
                    name: 'Date Field',
                    selection: menuSelections.value['Date Field'],
                    options: [
                        [{
                                title: 'Date Field',
                                type: 'menu',
                                showSelected: true,
                                selected: menuSelections.value['Date Field'],
                                options: props.dateFields.map(({ name }) => name)
                            }]
                    ],
                }, {
                    name: 'Resolution',
                    selection: menuSelections.value['Resolution'],
                    options: [
                        [{
                                title: 'Resolution',
                                type: 'menu',
                                showSelected: true,
                                selected: menuSelections.value['Resolution'],
                                options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']
                            }]
                    ],
                }];
            return options;
        });
        const seriesFor = (sentiment) => {
            return timelineSeries.value.filter((series) => series.name.startsWith(sentiment));
        };
        const getTrendEl = () => {
            return root.value?.$el.querySelector('div.content');
        };
        const csvData = () => {
            return getCsvData(timelineSeries.value, menuSelections.value);
        };
        const getExportConfig = () => {
            return {
                dims: getTrendEl()?.getBoundingClientRect(),
                css: `
          text {
            color: #383838;
            font-size: 14px;
            stroke: none;
          }
          .line {
            stroke-width: 2px;
          }
          .axis path, .axis line {
            shape-rendering: crispEdges;
            stroke: #ebebeb;
            stroke-width: 2px;
            opacity: 0.5;
          }
        `
            };
        };
        const updateConfig = () => {
            const options = {
                resolution: menuSelections.value['Resolution'],
                dateField: menuSelections.value['Date Field'],
                display: menuSelections.value['Display'],
            };
            const updated = Object.assign({}, props.config, { options });
            emit('config-changed', updated);
        };
        const setOptionsFromConfig = () => {
            const resolution = props.config?.options?.resolution ?? 'Monthly';
            const dateField = props.config?.options?.dateField ?? (props.defaultDateField ?? (hasDate.value && props.dateFields[0].name));
            const display = props.config?.options?.display ?? 'Values';
            setMenuSelection(null, ['Resolution', resolution], true);
            setMenuSelection(null, ['Date Field', dateField], true);
            setMenuSelection(null, ['Display', display], true);
        };
        const setMenuSelection = (_, path, fromConfig = false) => {
            menuSelections.value[path[0]] = path[1];
            if (!fromConfig)
                updateConfig();
        };
        const calculateYRange = () => {
            const visibleDataset = timelineSeries.value.filter((series) => series.visible);
            const dataMax = getDataAbsMax(visibleDataset, displayOptions.value.yCapValue, displayOptions.value.yMultipleOf);
            yRange.value = [0, dataMax];
        };
        const fetchData = (force = false) => {
            const blocks = [{
                    'aggfuncs': [{
                            'new_column': 'frequency',
                            'src_column': 'document_id',
                            'aggfunc': 'count'
                        }],
                    'pivot_field': 'sentiment__',
                    'metric_calculator': 'sentiment'
                }];
            const requirements = {
                blocks,
                date_fieldname: menuSelections.value['Date Field'],
                date_aggregation_offset: getAggregationOffset(menuSelections.value['Resolution']),
                week_start: props.weekStart,
            };
            // Data by date resolution
            emit('requires', 'compare-sentiment-timeline-slice-one', requirements, force, props.sliceOneFilters);
            emit('requires', 'compare-sentiment-timeline-slice-two', requirements, force, props.sliceTwoFilters);
            // Overall data
            emit('requires', 'compare-sentiment-timeline-slice-one-total', {
                blocks
            }, force, props.sliceOneFilters);
            emit('requires', 'compare-sentiment-timeline-slice-two-total', {
                blocks
            }, force, props.sliceTwoFilters);
        };
        const regroupData = () => {
            if (!hasDate.value)
                return;
            records.value = {};
            timelineSeries.value = [];
            const process = (data, name, lineStyle) => {
                const [recs, ...groupedData] = regroup(data, menuSelections.value['Date Field'], false, props.group, name);
                const rows = groupedData.filter(({ counts = [] }) => counts.length > 0);
                if (lineStyle) {
                    rows.forEach((row) => {
                        row.lineStyle = lineStyle;
                    });
                }
                // Display value on slice one only
                rows.forEach((row) => {
                    row.lastPointLabel = lineStyle !== 'dashed-line';
                });
                timelineSeries.value.push(...rows);
                Object.assign(records.value, recs);
            };
            props.sliceOneData?.data &&
                process(props.sliceOneData.data, ` (${props.sliceOneName})`);
            props.sliceTwoData?.data &&
                process(props.sliceTwoData.data, ` (${props.sliceTwoName})`, 'dashed-line');
            calculateYRange();
        };
        const makePptSlide = (pptx) => {
            const slide = pptx.addSlide();
            makeTimelineSlide(pptx, slide, timelineSeries.value.map((s, i) => ({
                ...s,
                // Shift the colour of slice two lines
                color: i > 3 ? adjustColor(s.color, 30) : s.color,
                counts: s.counts.map((c) => c * 100),
            })), props.exportName + ' - Compare Sentiment Timeline', 'Sentiment Category (%)', props.dayFirstDates, {
                valAxisMaxVal: 100,
            });
        };
        onMounted(() => {
            if (hasDate.value) {
                menuSelections.value['Date Field'] = props.defaultDateField ?? props.dateFields[0].name;
            }
            setOptionsFromConfig();
            if (props.sliceOneData?.data === null ||
                props.sliceTwoData?.data === null) {
                fetchData();
            }
            else {
                regroupData();
            }
        });
        watch(() => props.config, () => {
            setOptionsFromConfig();
        }, {
            deep: true,
        });
        watch(() => [
            props.sliceOneFilters,
            props.sliceTwoFilters,
            menuSelections.value,
            props.group,
        ], () => {
            fetchData();
        }, {
            deep: true,
        });
        watch(() => menuSelections.value['Display'], () => {
            regroupData();
        });
        watch(() => [
            props.sliceOneData,
            props.sliceTwoData,
        ], () => {
            regroupData();
        }, {
            deep: true,
        });
        return {
            icon,
            errorIcon,
            refresh,
            contact,
            reload,
            displayOptions,
            menuSelections,
            querySentiment,
            hasDate,
            root,
            getTrendEl,
            csvData,
            getExportConfig,
            menus,
            setMenuSelection,
            timelineSeries,
            yRange,
            records,
            calculateYRange,
            isLoading,
            error,
            seriesFor,
            chartHeight: '160px',
            makePptSlide,
        };
    }
});
